import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Autocomplete,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import { searchGraphUsers } from "../api/usersApi";
import { loadLocations } from "../api/scheduleApi";

const AddUserDialog = ({ open, onClose, onSubmit, fetchToken, saving }) => {
  const initialForm = { email: "", display_name: "", role: "", location: "" };

  const [form, setForm] = useState(initialForm);
  const [options, setOptions] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setOptions([]);
      setSearchTerm("");
      setErrorMsg("");
    }
  }, [open]);

  // Load locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const token = await fetchToken();
        const data = await loadLocations(token);
        setLocations(Array.isArray(data) ? data.sort() : []);
      } catch (err) {
        console.error("❌ Failed to load locations:", err);
      } finally {
        setLoadingLocations(false);
      }
    };
    if (open) fetchLocations();
  }, [open, fetchToken]);

  // Debounce Graph search
  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (searchTerm && searchTerm.length >= 2) {
        try {
          setLoadingSearch(true);
          const token = await fetchToken();
          const results = await searchGraphUsers(searchTerm, token);
          setOptions(results || []);
        } catch (err) {
          console.error("❌ Graph search failed:", err);
        } finally {
          setLoadingSearch(false);
        }
      } else {
        setOptions([]);
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm, fetchToken]);

  // Select user
  const handleSelectUser = (_, user) => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        email: user.email || "",
        display_name: user.name || "",
      }));
    } else {
      setForm((prev) => ({ ...prev, email: "", display_name: "" }));
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    if (!form.email || !form.role) {
      setErrorMsg("Email and Role are required.");
      return;
    }
    if (form.role === "SiteAdmin" && !form.location) {
      setErrorMsg("Location is required for Site Admins.");
      return;
    }
    setErrorMsg("");
    onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New User</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={options}
              loading={loadingSearch}
              getOptionLabel={(option) =>
                `${option.name || ""} (${option.email || ""})`
              }
              onInputChange={(_, value) => setSearchTerm(value)}
              onChange={handleSelectUser}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search User"
                  placeholder="Type name or email"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSearch ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Display Name"
              name="display_name"
              fullWidth
              value={form.display_name}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Email"
              name="email"
              fullWidth
              value={form.email}
              InputProps={{ readOnly: true }}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Role"
              name="role"
              fullWidth
              value={form.role}
              onChange={handleChange}
              required
              disabled={saving}
            >
              <MenuItem value="Administrator">Administrator</MenuItem>
              <MenuItem value="SiteAdmin">Site Admin</MenuItem>
            </TextField>
          </Grid>

          {form.role === "SiteAdmin" && (
            <Grid item xs={12}>
              <TextField
                select
                label="Location"
                name="location"
                fullWidth
                value={form.location}
                onChange={handleChange}
                required
                disabled={loadingLocations || saving}
                helperText={
                  loadingLocations
                    ? "Loading locations..."
                    : locations.length === 0
                    ? "No locations available"
                    : ""
                }
              >
                {locations.map((loc) => (
                  <MenuItem key={loc} value={loc}>
                    {loc}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>

        {errorMsg && (
          <Box mt={2}>
            <Alert severity="warning">{errorMsg}</Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {saving ? "Saving..." : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserDialog;
